import React from 'react';
import { Link } from 'react-router-dom';
import {
  PlayIcon,
  PauseIcon,
  ArrowsPointingOutIcon,
  QueueListIcon,
  PhoneXMarkIcon,
} from '@heroicons/react/24/outline';
import MediaWarning from './MediaWarning';
import ScreeningNotice from './ScreeningNotice';
import { SESSION_STATUS } from '../../stores/callSessionStore';
// Deliberadamente el MISMO componente que usa la pantalla de sesión: los siete
// resultados posibles tienen que vivir en un solo sitio o acaban divergiendo.
import OutcomeButtons from '../../pages/CallSessionPage/components/OutcomeButtons';
import AccionesTrasTipificar from '../../pages/CallSessionPage/components/AccionesTrasTipificar';

/**
 * La barra de sesión que acompaña al gestor por toda la aplicación.
 *
 * Existe porque la sesión ya no muere al navegar: si el gestor puede estar en la
 * pantalla de expedientes cuando entra una llamada, **tiene que poder ver quién
 * es y cerrarla sin moverse**. Sin esto, una llamada que se descuelga sola
 * mientras él mira otra cosa deja al deudor hablando con un micro abierto y a
 * nadie escuchando, que es peor que no haber permitido navegar.
 *
 * Es deliberadamente más pobre que la pantalla completa: aquí no va el resumen
 * del expediente ni el histórico. Lo imprescindible para no dejar colgado a
 * nadie, y un enlace para volver al puesto de trabajo de verdad.
 */

const LABELS = {
  [SESSION_STATUS.CONNECTING]: { text: 'Conectando…', dot: 'bg-amber-400 animate-pulse' },
  [SESSION_STATUS.READY]: { text: 'Listo', dot: 'bg-green-500' },
  [SESSION_STATUS.DIALING]: { text: 'Llamando', dot: 'bg-blue-500 animate-pulse' },
  [SESSION_STATUS.CONNECTED]: { text: 'En conversación', dot: 'bg-blue-600' },
  [SESSION_STATUS.WRAP]: { text: 'Cerrando', dot: 'bg-blue-300' },
  [SESSION_STATUS.PAUSED]: { text: 'En pausa', dot: 'bg-amber-500' },
};

const euros = (amount) =>
  typeof amount === 'number'
    ? new Intl.NumberFormat('es-ES', {
        style: 'currency',
        currency: 'EUR',
        maximumFractionDigits: 0,
      }).format(amount)
    : null;

const CallSessionDock = ({
  status,
  current,
  pendingOutcome,
  queueDepth,
  mediaWarning,
  screening,
  onPause,
  onResume,
  onOutcome,
  onHangup,
  accionesTrasTipificar = null,
  onCrearTarea,
  onCancelarProgramada,
  onCerrarAcciones,
}) => {
  const label = LABELS[status] || LABELS[SESSION_STATUS.READY];
  const paused = status === SESSION_STATUS.PAUSED;
  const enConversacion = status === SESSION_STATUS.CONNECTED;
  const quien = current || pendingOutcome;

  return (
    <div className='fixed inset-x-0 bottom-0 z-50 border-t border-gray-200 bg-white shadow-lg'>
      <div className='mx-auto flex max-w-7xl flex-wrap items-center gap-x-6 gap-y-3 px-4 py-3'>
        <span className='flex items-center gap-2 text-sm font-medium text-gray-800'>
          <span className={`h-2.5 w-2.5 rounded-full ${label.dot}`} aria-hidden='true' />
          {label.text}
        </span>

        {quien && (
          <span className='text-sm text-gray-600'>
            <span className='font-medium text-gray-900'>{quien.debtorName || 'Sin nombre'}</span>
            {euros(quien.debt) && <span className='ml-2 text-gray-500'>{euros(quien.debt)}</span>}
          </span>
        )}

        {typeof queueDepth === 'number' && (
          <span className='flex items-center gap-1.5 text-sm text-gray-500'>
            <QueueListIcon className='h-4 w-4' aria-hidden='true' />
            {queueDepth} en cola
          </span>
        )}

        {mediaWarning && <MediaWarning warning={mediaWarning} compact />}
        <ScreeningNotice screening={screening} compact />

        <div className='ml-auto flex items-center gap-2'>
          {/*
            Colgar tambien desde aqui: si el gestor esta en otra pantalla y le
            entra un deudor que no cuelga, obligarle a volver a /call-session
            para cortar seria justo la friccion que esta barra existe para
            quitar.
          */}
          {enConversacion && (
            <button
              type='button'
              onClick={onHangup}
              className='inline-flex items-center gap-1.5 rounded-lg border border-red-300 bg-red-50 px-3 py-1.5 text-sm font-medium text-red-700 hover:bg-red-100'
            >
              <PhoneXMarkIcon className='h-4 w-4' aria-hidden='true' />
              Colgar
            </button>
          )}

          <button
            type='button'
            onClick={paused ? onResume : onPause}
            className='inline-flex items-center gap-1.5 rounded-lg border border-gray-300 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50'
          >
            {paused ? (
              <>
                <PlayIcon className='h-4 w-4' aria-hidden='true' />
                Reanudar
              </>
            ) : (
              <>
                <PauseIcon className='h-4 w-4' aria-hidden='true' />
                Pausar
              </>
            )}
          </button>

          {/*
            No hay botón de CERRAR sesión aquí, y es a propósito: cerrarla por
            error desde una barra que te acompaña a todas partes costaría volver
            a activarla y perder el hueco. Se cierra desde su pantalla.
          */}
          <Link
            to='/call-session'
            className='inline-flex items-center gap-1.5 rounded-lg border border-gray-300 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50'
          >
            <ArrowsPointingOutIcon className='h-4 w-4' aria-hidden='true' />
            Ir a la sesión
          </Link>
        </div>

        {/*
          La tipificación pendiente ocupa toda la fila de abajo. Es lo único que
          el gestor DEBE poder hacer sin volver a la pantalla: si no, la llamada
          anterior se queda sin cerrar y el expediente vuelve a la cola.
        */}
        {pendingOutcome && (
          <div className='w-full border-t border-gray-100 pt-3'>
            <OutcomeButtons onSelect={onOutcome} pendingFor={pendingOutcome.debtorName} />
          </div>
        )}

        {/*
          Y las acciones de después, también aquí: el gestor que tipifica desde
          la barra sin volver a la pantalla tiene que poder poner la fecha que
          acaba de acordar. Si solo salieran en `/call-session`, «crear tarea»
          existiría únicamente para quien está mirando esa pestaña.
        */}
        {accionesTrasTipificar && (
          <div className='w-full border-t border-gray-100 pt-3'>
            <AccionesTrasTipificar
              acciones={accionesTrasTipificar}
              onCrearTarea={onCrearTarea}
              onCancelarProgramada={onCancelarProgramada}
              onCerrar={onCerrarAcciones}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default CallSessionDock;
